import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useWallet } from '@txnlab/use-wallet-react';
import { Chrome, Wallet, ArrowRight, X, Fingerprint, Mail, MessageSquare, ExternalLink, Smartphone, Shield, Coins } from 'lucide-react';

interface AuthModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function AuthModal({ isOpen, onClose }: AuthModalProps) {
    const { 
        signInWithGoogle, 
        signInWithPasskey,
        signInWithEmail,
        signUpWithEmail,
        signInWithDiscord,
        activeAccount,
        socialUser,
        isVerified,
        isVerifying,
        verifyAccount,
        preferredAuthView,
        setPreferredAuthView,
        userProfile
    } = useAuth();
    const { wallets } = useWallet();
    
    const [view, setView] = useState<'options' | 'wallet' | 'email' | 'why-wallet' | 'verify'>(preferredAuthView || 'options');
    const [isLoading, setIsLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isSignUp, setIsSignUp] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [connectingWallet, setConnectingWallet] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) setView(preferredAuthView as any);
    }, [preferredAuthView, isOpen]);

    useEffect(() => {
        if (activeAccount && isOpen) {
            setConnectingWallet(null);
            if (!isVerified) {
                setView('verify');
            } else {
                onClose();
            }
        }
    }, [activeAccount, isVerified, isOpen, onClose]);

    useEffect(() => {
        if (!isOpen) { 
            setPreferredAuthView('options');
            setError(null);
            setEmail('');
            setPassword('');
            setConnectingWallet(null);
        }
    }, [isOpen, setPreferredAuthView]);

    const handleGoogleSignIn = async () => {
        setIsLoading(true);
        setError(null);
        try {
            await signInWithGoogle();
            onClose();
        } catch (err: any) {
            setError(err.message || 'Google sign-in failed');
        } finally {
            setIsLoading(false);
        }
    };

    const handlePasskeySignIn = async () => {
        setIsLoading(true);
        setError(null);
        try {
            await signInWithPasskey();
            onClose();
        } catch (err: any) {
            setError(err.message || 'Passkey failed');
        } finally {
            setIsLoading(false);
        }
    };

    const handleEmailAuth = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email || !password) return;
        setIsLoading(true);
        setError(null);
        try {
            if (isSignUp) {
                await signUpWithEmail(email, password);
            } else {
                await signInWithEmail(email, password);
            }
            onClose();
        } catch (err: any) {
            setError(err.message || 'Email authentication failed');
        } finally {
            setIsLoading(false);
        }
    };

    const handleWalletConnect = async (walletId: string, walletName: string) => {
        const wallet = wallets.find(w => w.id === walletId);
        if (!wallet) {
            setError(`${walletName} wallet not available`);
            return;
        }
        setConnectingWallet(walletId);
        setError(null);
        try {
            await wallet.connect();
        } catch (err: any) {
            setError(err?.message || `Failed to connect to ${walletName}`);
            setConnectingWallet(null);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[99999] flex justify-center items-start pt-[100px] px-4 pb-8 bg-black/85 backdrop-blur-xl overflow-y-auto">
            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="glassmorphism w-full max-w-[400px] rounded-[2.5rem] shadow-[0_40px_80px_rgba(0,0,0,0.8)] overflow-hidden border-t-8 border-t-pit-green"
            >
                {/* Header */}
                <div className="relative p-8 border-b border-white/5">
                    <button
                        onClick={onClose}
                        className="absolute top-6 right-6 w-8 h-8 rounded-full bg-white/5 border-none cursor-pointer flex items-center justify-center hover:bg-white/10 transition-colors text-slate-400"
                    >
                        <X size={18} />
                    </button>
                    <div className="space-y-4">
                        <div className="flex items-center gap-3">
                            <span className="text-3xl">🥒</span>
                            <h2 className="text-2xl font-black italic text-white uppercase tracking-tighter leading-none">
                                {view === 'options' ? 'Welcome to ' : 
                                 view === 'wallet' ? 'Connect ' : 
                                 view === 'why-wallet' ? 'Why a ' :
                                 isSignUp ? 'Create ' : 'Sign into '}
                                <span className="text-pit-green">The Pit</span>
                            </h2>
                        </div>
                        {socialUser && (
                            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-pit-green/10 border border-pit-green/20 w-fit animate-in fade-in slide-in-from-left-2 duration-500">
                                <div className="w-2 h-2 rounded-full bg-pit-green animate-pulse" />
                                <span className="text-[10px] font-black uppercase tracking-widest text-[#39FF14]">
                                    Logged in as {userProfile?.displayName || socialUser.displayName || socialUser.email?.split('@')[0] || 'Operator'}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <div style={{ padding: '24px' }}>
                    <AnimatePresence mode="wait">

                        {/* ── Main Options View ─────────────────────────────── */}
                        {view === 'options' && (
                            <motion.div
                                key="options"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
                            >
                                {/* ── WALLET — Recommended / Top Priority ── */}
                                <div className="relative">
                                    {/* Recommended badge */}
                                    <div className="absolute -top-2.5 left-4 z-10">
                                        <span className="bg-pit-green text-black text-[9px] font-black uppercase tracking-widest px-2.5 py-0.5 rounded-full shadow-[0_0_12px_rgba(57,255,20,0.5)]">
                                            ★ Recommended
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => setView('wallet')}
                                        className="w-full flex items-center gap-4 p-4 pt-5 rounded-2xl bg-pit-green/5 border border-pit-green/30 hover:border-pit-green/60 hover:bg-pit-green/10 transition-all text-left group shadow-[0_0_20px_rgba(57,255,20,0.05)]"
                                    >
                                        <div className="w-10 h-10 rounded-xl bg-pit-green/10 flex items-center justify-center border border-pit-green/30 group-hover:border-pit-green/60 transition-all">
                                            <Wallet size={20} className="text-pit-green" />
                                        </div>
                                        <div className="flex-1">
                                            <div className="text-white font-black text-sm uppercase tracking-tight leading-none mb-1 group-hover:text-pit-green transition-colors">Algorand Wallet</div>
                                            <div className="text-[10px] text-slate-400 font-bold uppercase tracking-widest leading-none">Save stats, own items, trade gears</div>
                                        </div>
                                        <ArrowRight size={18} className="text-pit-green/50 group-hover:text-pit-green group-hover:translate-x-1 transition-all" />
                                    </button>
                                </div>

                                {/* Why wallet upsell link */}
                                <button
                                    onClick={() => setView('why-wallet')}
                                    className="flex items-center justify-center gap-1.5 text-[10px] text-slate-500 font-bold uppercase tracking-widest hover:text-pit-green transition-colors py-1"
                                >
                                    <span>Don't have a pass? See why it's worth it</span>
                                    <ArrowRight size={10} />
                                </button>

                                {/* Divider */}
                                <div className="flex items-center gap-3 my-1">
                                    <div className="flex-1 h-px bg-white/5" />
                                    <span className="text-[9px] text-slate-600 font-black uppercase tracking-widest">or continue with</span>
                                    <div className="flex-1 h-px bg-white/5" />
                                </div>

                                {/* Social options */}
                                <AuthOption 
                                    icon={<Chrome size={20} className="text-red-400" />}
                                    title="Google"
                                    description="Sign in with Google"
                                    onClick={handleGoogleSignIn}
                                    isLoading={isLoading}
                                />
                                <AuthOption 
                                    icon={<MessageSquare size={20} className="text-[#5865F2]" />}
                                    title="Discord"
                                    description="Sign in with Discord"
                                    onClick={signInWithDiscord}
                                />
                                <AuthOption 
                                    icon={<Mail size={20} className="text-blue-400" />}
                                    title="Email & Password"
                                    description="Sign in with email"
                                    onClick={() => setView('email')}
                                />
                                <AuthOption 
                                    icon={<Fingerprint size={20} className="text-amber-400" />}
                                    title="Passkey"
                                    description="Biometrics login"
                                    onClick={handlePasskeySignIn}
                                    isLoading={isLoading}
                                />

                                {/* Manual Guest option removed - now automatic */}
                            </motion.div>
                        )}

                        {/* ── Why Wallet View ───────────────────────────────── */}
                        {view === 'why-wallet' && (
                            <motion.div
                                key="why-wallet"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                className="flex flex-col gap-5"
                            >
                                <button onClick={() => setView('options')} className="text-[10px] text-slate-500 font-bold uppercase tracking-wider flex items-center gap-1 hover:text-white transition-colors">
                                    ← Back
                                </button>

                                <p className="text-slate-400 text-xs font-medium leading-relaxed">
                                    A wallet is your key to the full PicklePong experience. Social logins play the game — wallet users <span className="text-white font-bold">own</span> it.
                                </p>

                                {/* Benefits */}
                                <div className="flex flex-col gap-3">
                                    {[
                                        { icon: <Coins size={16} className="text-yellow-400" />, title: 'Exclusive Gear', desc: 'Equip rare item drops and legendary paddles as your identity' },
                                        { icon: <Shield size={16} className="text-pit-green" />, title: 'True Ownership', desc: 'Your arenas, items, and cosmetics live on-chain — they\'re yours forever' },
                                        { icon: <Coins size={16} className="text-blue-400" />, title: 'In-Game Economy', desc: 'Top up credits instantly with USDC — no middleman, instant settlement' },
                                    ].map(b => (
                                        <div key={b.title} className="flex items-start gap-3 p-3 rounded-xl bg-white/5 border border-white/5">
                                            <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center shrink-0 mt-0.5">{b.icon}</div>
                                            <div>
                                                <p className="text-white text-xs font-black uppercase tracking-tight mb-0.5">{b.title}</p>
                                                <p className="text-slate-500 text-[10px] font-medium leading-tight">{b.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Pera CTA */}
                                <div className="p-4 rounded-2xl bg-pit-green/5 border border-pit-green/20">
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="w-10 h-10 rounded-xl overflow-hidden bg-black border border-pit-green/20 flex items-center justify-center shrink-0">
                                            <Smartphone size={20} className="text-pit-green" />
                                        </div>
                                        <div>
                                            <p className="text-white text-xs font-black uppercase tracking-tight">Pera (Mobile Pass)</p>
                                            <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest">Best for beginners · Free · 2 min setup</p>
                                        </div>
                                    </div>
                                    <a
                                        href="https://perawallet.app"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-pit-green text-black font-black uppercase text-[10px] tracking-widest hover:brightness-110 transition-all shadow-[0_0_20px_rgba(57,255,20,0.2)]"
                                    >
                                        <span>Get the Pass</span>
                                        <ExternalLink size={12} />
                                    </a>
                                </div>

                                <button
                                    onClick={() => setView('wallet')}
                                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-white/5 border border-white/10 hover:border-pit-green/30 text-white font-black uppercase text-[10px] tracking-widest transition-all"
                                >
                                    I already have a pass →
                                </button>
                            </motion.div>
                        )}

                        {/* ── Email View ────────────────────────────────────── */}
                        {view === 'email' && (
                            <motion.div
                                key="email"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                            >
                                <button onClick={() => setView('options')} className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-6 flex items-center gap-1 hover:text-white transition-colors">
                                    ← Back
                                </button>

                                <form onSubmit={handleEmailAuth} className="flex flex-col gap-4">
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-[10px] font-black uppercase text-slate-500 tracking-widest pl-1">Email Address</label>
                                        <input 
                                            type="email" 
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="you@example.com"
                                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:border-[#39FF14]/50 focus:outline-none transition-all"
                                            required
                                        />
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label className="text-[10px] font-black uppercase text-slate-500 tracking-widest pl-1">Password</label>
                                        <input 
                                            type="password" 
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            placeholder="••••••••"
                                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:border-[#39FF14]/50 focus:outline-none transition-all"
                                            required
                                        />
                                    </div>

                                    {error && (
                                        <div className="text-[10px] text-red-400 font-bold uppercase tracking-tight bg-red-400/10 border border-red-400/20 p-2 rounded-lg text-center">
                                            {error}
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={isLoading}
                                        className="w-full py-4 rounded-xl bg-[#39FF14] hover:bg-white text-black font-black uppercase text-xs transition-all shadow-[0_0_20px_rgba(57,255,20,0.2)] tracking-widest disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isLoading ? 'Processing...' : isSignUp ? 'Create Account' : 'Sign In'}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setIsSignUp(!isSignUp)}
                                        className="text-[10px] text-slate-500 font-bold uppercase tracking-widest hover:text-white transition-colors text-center"
                                    >
                                        {isSignUp ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
                                    </button>
                                </form>
                            </motion.div>
                        )}

                        {/* ── Wallet Connect View ───────────────────────────── */}
                        {view === 'wallet' && (
                            <motion.div
                                key="wallet"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
                            >
                                <button onClick={() => setView('options')} className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-4 flex items-center gap-1 hover:text-white transition-colors text-left w-full">
                                    ← Back to options
                                </button>

                                {error && (
                                    <div className="text-[10px] text-red-400 font-bold uppercase tracking-tight bg-red-400/10 border border-red-400/20 p-2 rounded-lg text-center mb-2">
                                        {error}
                                    </div>
                                )}

                                {wallets.map((wallet) => (
                                    <button
                                        key={wallet.id}
                                        onClick={() => handleWalletConnect(wallet.id, wallet.metadata.name)}
                                        disabled={connectingWallet === wallet.id}
                                        className="w-full flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/5 hover:border-[#39FF14]/30 hover:bg-white/10 transition-all text-left group disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <div className="w-10 h-10 rounded-xl bg-black/40 flex items-center justify-center border border-white/5 group-hover:border-[#39FF14]/20 transition-all overflow-hidden shrink-0">
                                            {connectingWallet === wallet.id ? (
                                                <div className="w-5 h-5 border-2 border-[#39FF14]/30 border-t-[#39FF14] rounded-full animate-spin" />
                                            ) : (
                                                <img src={wallet.metadata.icon} alt="" className="w-6 h-6 object-contain" />
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            <div className="text-white font-black text-sm uppercase tracking-tight group-hover:text-[#39FF14] transition-colors leading-none mb-1">
                                                {wallet.metadata.name}
                                            </div>
                                            <div className="text-[10px] text-slate-500 font-bold uppercase tracking-widest leading-none">
                                                Verified Wallet
                                            </div>
                                        </div>
                                        <ArrowRight size={16} className={`transition-colors ${connectingWallet === wallet.id ? 'text-[#39FF14]' : 'text-slate-800 group-hover:text-[#39FF14]'}`} />
                                    </button>
                                ))}

                                {/* No wallet yet? */}
                                <div className="mt-3 p-3 rounded-xl bg-white/3 border border-white/5 flex items-center justify-between">
                                    <span className="text-[10px] text-slate-600 font-bold uppercase tracking-widest">No wallet yet?</span>
                                    <a
                                        href="https://perawallet.app"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[10px] text-pit-green font-black uppercase tracking-widest hover:brightness-125 transition-all flex items-center gap-1"
                                    >
                                        Get Pera <ExternalLink size={10} />
                                    </a>
                                </div>
                            </motion.div>
                        )}

                        {/* ── Verify View ───────────────────────────── */}
                        {view === 'verify' && activeAccount && (
                            <motion.div
                                key="verify"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                className="flex flex-col gap-6 text-center py-4"
                            >
                                <div className="space-y-2">
                                    <div className="w-16 h-16 rounded-2xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center mx-auto mb-4">
                                        <Shield size={32} className="text-orange-500" />
                                    </div>
                                    <h3 className="text-xl font-black italic text-white uppercase tracking-tight">Verify Ownership</h3>
                                    <p className="text-slate-400 text-xs font-medium px-4">
                                        Prove ownership of <span className="text-orange-400 font-mono">{activeAccount.address.slice(0,6)}...{activeAccount.address.slice(-4)}</span> to sync your stats and level.
                                    </p>
                                </div>

                                <div className="space-y-3">
                                    <button
                                        onClick={verifyAccount}
                                        disabled={isVerifying}
                                        className="w-full py-4 rounded-2xl bg-orange-500 hover:bg-orange-400 text-black font-black uppercase text-sm transition-all shadow-[0_0_30px_rgba(249,115,22,0.3)] disabled:opacity-50"
                                    >
                                        {isVerifying ? 'Signing Challenge...' : 'Sign Challenge'}
                                    </button>
                                    
                                    <button 
                                        onClick={() => {
                                            // Fallback to options if they want to try something else
                                            setView('options');
                                        }} 
                                        className="text-[10px] text-slate-500 font-bold uppercase tracking-widest hover:text-white transition-colors"
                                    >
                                        Use a different method
                                    </button>
                                </div>

                                <div className="p-3 rounded-xl bg-orange-500/5 border border-orange-500/10">
                                    <p className="text-[9px] text-orange-500/60 font-bold uppercase tracking-tight">
                                        Free · No Gas · One-time signature
                                    </p>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </motion.div>
        </div>
    );
}

function AuthOption({ icon, title, description, onClick, isLoading }: any) {
    return (
        <button
            onClick={onClick}
            disabled={isLoading}
            className="w-full flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/5 hover:border-white/20 hover:bg-white/10 transition-all text-left group disabled:opacity-50"
        >
            <div className="w-10 h-10 rounded-xl bg-black/40 flex items-center justify-center border border-white/5 group-hover:border-white/10 transition-all">
                {isLoading ? (
                    <div className="w-5 h-5 border-2 border-[#39FF14]/30 border-t-[#39FF14] rounded-full animate-spin" />
                ) : icon}
            </div>
            <div className="flex-1">
                <div className="text-white font-black text-sm uppercase tracking-tight leading-none mb-1 group-hover:text-[#39FF14] transition-colors">{title}</div>
                <div className="text-[10px] text-slate-500 font-bold uppercase tracking-widest leading-none">{description}</div>
            </div>
            {!isLoading && <ArrowRight size={18} className="text-slate-800 group-hover:text-[#39FF14] group-hover:translate-x-1 transition-all" />}
        </button>
    );
}
